/* =============================================================================
   Heil Ginseng — shared root-age quiz engine
   -----------------------------------------------------------------------------
   ONE source of truth for the recommendation logic + Shopify variant URLs.
   Variant IDs lifted verbatim from scripts/build_meta_roots_phase1_bundle.py
   (RECOMMENDED_VARIANT_URLS) so the quiz always deep-links the correct variant.

   Each visual direction supplies its OWN markup + CSS and wires into this engine
   via data-attributes — so the look differs per direction but the logic does not.

   Required markup contract (per quiz block):
     [data-heil-quiz]
       [data-quiz-step]                      one per question (first is shown first)
         button[data-quiz-answer][data-key]  answer; data-key is a known key below
       [data-quiz-progress-bar]              (optional) width is set 0..100%
       [data-quiz-progress-label]            (optional) "Question X of N"
       [data-quiz-result]                    hidden until finished
         [data-quiz-result-title]
         [data-quiz-result-copy]
         [data-quiz-result-product]
         [data-quiz-result-meta]
         a[data-quiz-result-cta]             href is set to the exact variant URL
         [data-quiz-restart]                 (optional) button to retake
         [data-quiz-compare]                 (optional) link to compare ages

   Known answer keys (directions reuse these so routing stays consistent):
     purpose:    self | gift | stockup | compare
     experience: new | some | experienced | recipient
     preference: starter | balanced | premium | value
     size:       size_8oz | size_1lb | size_5lb | size_10lb
   ========================================================================== */
(function (global) {
  'use strict';

  var BASE = 'https://heilginseng.com/products/';
  var SCRIPT_SRC = document.currentScript && document.currentScript.src;
  var ASSET_BASE = SCRIPT_SRC
    ? SCRIPT_SRC.replace(/\/[^\/?]+(?:\?.*)?$/, '')
    : 'https://heilginseng.com/cdn/shop/t/68/assets';
  var Q3_THUMBS = {
    starter: {
      file: 'lp-q3-easy-starting-point.png',
      alt: 'Heil Harvest Wisconsin ginseng packaging with whole roots arranged for an approachable starter choice.'
    },
    balanced: {
      file: 'lp-q3-clear-step-up.png',
      alt: 'Heil Harvest Wisconsin ginseng packaging with whole roots and age-comparison styling for a clear step up.'
    },
    premium: {
      file: 'lp-q3-rare-premium-tier.png',
      alt: 'Heil Harvest Wisconsin ginseng packaging with whole roots in a premium gift-style presentation.'
    }
  };

  var VARIANTS = {
    three: {
      label: '3-Year Wisconsin Whole Roots',
      reason: 'An approachable first step for buyers who want genuine whole roots without overcommitting.',
      product: 'wisconsin-ginseng-whole-root-3-year',
      sizes: {
        size_8oz: ['8 oz', '42669572882528'],
        size_1lb: ['1 lb', '42554612187232'],
        size_5lb: ['5 lb', '42633933357152'],
        size_10lb: ['10 lb', '42661933056096']
      }
    },
    four: {
      label: '4-Year Wisconsin Whole Roots',
      reason: 'The balanced route for regular home use or a considered gift — a clear step up from entry roots.',
      product: 'wisconsin-ginseng-whole-root-4-year',
      sizes: {
        size_8oz: ['8 oz', '42669572915296'],
        size_1lb: ['1 lb', '42661933121632'],
        size_5lb: ['5 lb', '42661933154400'],
        size_10lb: ['10 lb', '42561262649440']
      }
    },
    five: {
      label: '5-Year Wisconsin Whole Roots',
      reason: 'The premium older-root signal — for meaningful gifts, experienced buyers, and serious stock-up.',
      product: 'wisconsin-ginseng-whole-root-5-year',
      sizes: {
        size_8oz: ['8 oz', '42669572948064'],
        size_1lb: ['1 lb', '42661933219936'],
        size_5lb: ['5 lb', '42661933252704'],
        size_10lb: ['10 lb', '42561262714976']
      }
    },
    blemish: {
      label: 'Blemish-Mix Wisconsin Whole Roots',
      reason: 'A value stock-up route for experienced buyers who care about farm-direct source more than perfect appearance.',
      product: 'wisconsin-ginseng-whole-root-blemish-mix',
      sizes: {
        size_8oz: ['5 lb', '42669575831648'],
        size_1lb: ['5 lb', '42669575831648'],
        size_5lb: ['5 lb', '42669575831648'],
        size_10lb: ['10 lb', '42561262682208']
      }
    }
  };

  function variantUrl(route, sizeKey) {
    var r = VARIANTS[route];
    var size = r.sizes[sizeKey] || r.sizes.size_1lb;
    return BASE + r.product + '?variant=' + size[1];
  }

  function variantId(route, sizeKey) {
    var r = VARIANTS[route];
    var size = r.sizes[sizeKey] || r.sizes.size_1lb;
    return size[1];
  }

  function cartAddUrl(route, sizeKey) {
    return '/cart/add?id=' + encodeURIComponent(variantId(route, sizeKey)) + '&quantity=1';
  }

  /* Deterministic routing with explicit priority (value+bulk > premium > starter > balanced). */
  function recommend(answers) {
    var s = {};
    answers.forEach(function (a) { s[a] = true; });

    var route = 'four';
    if (s.starter || s.new) route = 'three';
    if (s.premium || s.gift || s.recipient || s.experienced) route = 'five';
    // "Honest value / source over looks" is authoritative and evaluated LAST, so an
    // explicit value choice can never be overridden by the premium/experienced rule
    // above. Blemish-mix only stocks 5 lb / 10 lb, so value buyers who do NOT want a
    // bulk size go to the lowest-cost age tier (3-year) instead of being pushed up to
    // the premium 5-year price. (Regression-guarded by the value+small QA path.)
    if (s.value) route = (s.stockup || s.size_5lb || s.size_10lb) ? 'blemish' : 'three';

    var sizeOrder = ['size_8oz', 'size_1lb', 'size_5lb', 'size_10lb'];
    var size = sizeOrder.filter(function (k) { return s[k]; })[0] || 'size_1lb';
    if (s.stockup && size === 'size_8oz') size = 'size_5lb';
    // Blemish-mix is genuinely sold only in 5 lb / 10 lb. Clamp any smaller pick up to
    // 5 lb so the result never displays an 8 oz / 1 lb size the store cannot fulfill.
    if (route === 'blemish' && (size === 'size_8oz' || size === 'size_1lb')) size = 'size_5lb';

    var r = VARIANTS[route];
    var sizeLabel = (r.sizes[size] || r.sizes.size_1lb)[0];
    return {
      route: route,
      // Direction markup already shows a "Your match" eyebrow, so the title is just the
      // product label (no duplicated "Your match:" prefix).
      title: r.label,
      copy: r.reason,
      product: r.label + ' — ' + sizeLabel,
      sizeLabel: sizeLabel,
      meta: route === 'blemish'
        ? 'Our value mix is sold in 5 lb and 10 lb only — this adds the ' + sizeLabel + ' to your cart.'
        : 'This adds the exact size variant to your cart. You can review before checkout.',
      href: variantUrl(route, size),
      cartHref: cartAddUrl(route, size),
      variantId: variantId(route, size)
    };
  }

  function initOne(root) {
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]'));
    var result = root.querySelector('[data-quiz-result]');
    var bar = root.querySelector('[data-quiz-progress-bar]');
    var label = root.querySelector('[data-quiz-progress-label]');
    if (!steps.length || !result) return;
    patchQ3Thumbnails(steps);

    var answers = [];
    var total = steps.length;

    function setProgress(idx) {
      var pct = Math.round((idx / total) * 100);
      if (bar) bar.style.width = pct + '%';
      if (label) label.textContent = 'Question ' + Math.min(idx + 1, total) + ' of ' + total;
    }

    function show(idx) {
      steps.forEach(function (st, i) { st.classList.toggle('is-active', i === idx); });
      setProgress(idx);
    }

    function finish() {
      var rec = recommend(answers);
      var set = function (sel, val, attr) {
        var el = result.querySelector(sel);
        if (!el) return;
        if (attr) el.setAttribute(attr, val); else el.textContent = val;
      };
      set('[data-quiz-result-title]', rec.title);
      set('[data-quiz-result-copy]', rec.copy);
      set('[data-quiz-result-product]', rec.product);
      set('[data-quiz-result-meta]', rec.meta);
      set('[data-quiz-result-cta]', rec.cartHref, 'href');
      set('[data-quiz-result-cta]', rec.href, 'data-product-url');
      set('[data-quiz-result-cta]', rec.variantId, 'data-variant-id');
      set('[data-quiz-result-detail]', rec.href, 'href');
      steps.forEach(function (st) { st.classList.remove('is-active'); });
      result.classList.add('is-active');
      if (bar) bar.style.width = '100%';
      if (label) label.textContent = 'Done';
      root.setAttribute('data-quiz-done', 'true');
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quiz-answer]');
      if (btn && root.contains(btn)) {
        var key = btn.getAttribute('data-key');
        if (key) answers.push(key);
        var current = steps.findIndex(function (st) { return st.classList.contains('is-active'); });
        if (current + 1 < steps.length) { show(current + 1); } else { finish(); }
        return;
      }
      if (e.target.closest('[data-quiz-restart]')) {
        answers.length = 0;
        result.classList.remove('is-active');
        root.removeAttribute('data-quiz-done');
        show(0);
      }
    });

    show(0);
  }

  function patchQ3Thumbnails(steps) {
    var step = steps.filter(function (candidate) {
      var title = candidate.querySelector('.q__q');
      return title && /Which root sounds most like you\\?/i.test(title.textContent || '');
    })[0];
    if (!step) return;

    Object.keys(Q3_THUMBS).forEach(function (key) {
      var img = step.querySelector('[data-key="' + key + '"] .ans__thumb');
      var thumb = Q3_THUMBS[key];
      if (!img || !thumb) return;
      img.src = ASSET_BASE + '/' + thumb.file;
      img.width = 1254;
      img.height = 1254;
      img.alt = thumb.alt;
      img.setAttribute('data-q3-thumb', thumb.file);
    });
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-heil-quiz]')).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.HeilQuiz = { recommend: recommend, variantUrl: variantUrl, variantId: variantId, cartAddUrl: cartAddUrl, VARIANTS: VARIANTS, init: init };
})(window);


/* =============================================================================
   LP funnel tracking (appended by port_to_shopify.py; NOT in the bundle engine)
   -----------------------------------------------------------------------------
   Layered on top of the verbatim engine above via DOM events + the
   data-quiz-done flag, so the recommendation logic stays byte-identical to the
   source of truth. Fires quiz_start / quiz_complete / quiz_cta_click to
   dataLayer (GTM GT-TNL8JK2J), gtag (GA4 G-V5V9D1TRYF) and fbq (Meta Pixel
   1434396548313112). Every call is guarded and no-ops if that tag is absent.
   ========================================================================== */
(function () {
  'use strict';
  function track(name, params) {
    params = params || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params); } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        // quiz_start -> QuizStart (Meta custom-event convention)
        var fbName = name.replace(/(^|_)([a-z])/g, function (m, a, b) { return b.toUpperCase(); });
        window.fbq('trackCustom', fbName, params);
      }
    } catch (e) {}
  }
  function rootLabel(href) {
    if (!href) return 'unknown';
    if (href.indexOf('-3-year') !== -1) return '3-year';
    if (href.indexOf('-4-year') !== -1) return '4-year';
    if (href.indexOf('-5-year') !== -1) return '5-year';
    if (href.indexOf('blemish') !== -1) return 'blemish-mix';
    return 'unknown';
  }
  function wire(quiz) {
    var started = false;
    quiz.addEventListener('click', function (e) {
      if (e.target.closest('[data-quiz-answer]') && !started) {
        started = true;
        track('quiz_start', { quiz: 'root-age' });
      }
      var cta = e.target.closest('[data-quiz-result-cta]');
      if (cta) track('quiz_cta_click', { quiz: 'root-age', root: rootLabel(cta.getAttribute('data-product-url') || cta.getAttribute('href')) });
    });
    var obs = new MutationObserver(function () {
      if (quiz.getAttribute('data-quiz-done') === 'true') {
        obs.disconnect();
        var cta = quiz.querySelector('[data-quiz-result-cta]');
        track('quiz_complete', { quiz: 'root-age', root: rootLabel(cta && (cta.getAttribute('data-product-url') || cta.getAttribute('href'))) });
      }
    });
    obs.observe(quiz, { attributes: true, attributeFilter: ['data-quiz-done'] });
  }
  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-heil-quiz]')).forEach(wire);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
